(() => {
  'use strict';

  /**
   * AOCTANT place search: the matching logic behind the request form's city and country fields
   * (checklist 4b). No DOM here; assets/aoctant-map-request.js owns the form behaviour.
   *
   * Data: assets/aoctant-places.json (built by tools/places/build-places.mjs from GeoNames).
   * Matching ignores capitals, accents and punctuation, so "barcelona", "BARCELONA" and
   * "Barcelóna" are the same name. The list holds cities of 15,000+ people, so a miss is a
   * prompt to check the spelling, never a final "no": the Map Builder can still find
   * neighborhoods and small towns.
   */

  const fold = (value) =>
    String(value || '')
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/ß/g, 'ss')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();

  // Same pattern the Map Builder accepts (launch/geocode.mjs parseCoordinates).
  const coordinatePattern = /^(-?\d{1,2}(?:\.\d+)?)\s*°?\s*([NS])?\s*[,;\s]\s*(-?\d{1,3}(?:\.\d+)?)\s*°?\s*([EW])?$/i;
  const isCoordinates = (text) => coordinatePattern.test(String(text || '').trim());

  // A city fills the country by itself only when it clearly outweighs every other place of that name.
  const DOMINANCE = 3;

  const createIndex = (data) => {
    const countries = new Map();
    const countryKeys = new Map();
    Object.entries((data && data.countries) || {}).forEach(([code, names]) => {
      const keys = names.map(fold).filter(Boolean);
      countries.set(code, { code, name: names[0], keys });
      keys.forEach((key) => {
        if (!countryKeys.has(key)) countryKeys.set(key, code);
      });
      countryKeys.set(code.toLowerCase(), code);
    });
    const places = ((data && data.places) || []).map(([name, code, population], rank) => ({
      name,
      code,
      population,
      rank,
      key: fold(name),
    }));
    const byKey = new Map();
    places.forEach((place) => {
      if (!byKey.has(place.key)) byKey.set(place.key, []);
      byKey.get(place.key).push(place);
    });
    return { countries, countryKeys, places, byKey };
  };

  const countryName = (index, code) => (index.countries.get(code) || {}).name || '';
  const view = (index, place) => (place ? { name: place.name, code: place.code, country: countryName(index, place.code) } : null);

  /** Country code for typed text ("spain", "USA", "ES"), or null. */
  const countryCode = (index, text) => {
    const key = fold(text);
    return key ? index.countryKeys.get(key) || null : null;
  };

  /** "Barcelona, Spain" → { name: "Barcelona", country: "ES" } when the part after the comma is a country. */
  const splitPlace = (index, text) => {
    const raw = String(text || '');
    const comma = raw.lastIndexOf(',');
    if (comma > 0) {
      const code = countryCode(index, raw.slice(comma + 1));
      if (code) return { name: raw.slice(0, comma).trim(), country: code };
    }
    return { name: raw.trim(), country: null };
  };

  // Optimal string alignment distance with an early exit once it passes `limit`.
  const distance = (a, b, limit) => {
    if (Math.abs(a.length - b.length) > limit) return limit + 1;
    let prev2 = null;
    let prev = Array.from({ length: b.length + 1 }, (_, j) => j);
    for (let i = 1; i <= a.length; i += 1) {
      const row = [i];
      let best = i;
      for (let j = 1; j <= b.length; j += 1) {
        const cost = a[i - 1] === b[j - 1] ? 0 : 1;
        let value = Math.min(prev[j] + 1, row[j - 1] + 1, prev[j - 1] + cost);
        if (prev2 && i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
          value = Math.min(value, prev2[j - 2] + 1);
        }
        row[j] = value;
        if (value < best) best = value;
      }
      if (best > limit) return limit + 1;
      prev2 = prev;
      prev = row;
    }
    return prev[b.length];
  };

  const typoLimit = (key) => (key.length <= 4 ? 1 : key.length <= 8 ? 2 : 3);

  /** Closest name within a few typos ("barcilona" → Barcelona), largest place first on a tie. */
  const closestPlace = (index, key, code) => {
    const limit = typoLimit(key);
    let found = null;
    let foundDistance = limit + 1;
    for (const place of index.places) {
      if (code && place.code !== code) continue;
      const d = distance(key, place.key, limit);
      if (d < foundDistance) {
        found = place;
        foundDistance = d;
        if (d === 1) break; // places are sorted largest first, so the first one-typo match wins
      }
    }
    return foundDistance <= limit ? found : null;
  };

  const closestCountry = (index, key) => {
    const limit = typoLimit(key);
    let found = null;
    let foundDistance = limit + 1;
    index.countries.forEach((country) => {
      country.keys.forEach((countryKey) => {
        const d = distance(key, countryKey, limit);
        if (d < foundDistance) {
          found = country.code;
          foundDistance = d;
        }
      });
    });
    return found;
  };

  /** Up to `limit` places whose name starts with the typed text, largest first. */
  const suggestPlaces = (index, text, { country = '', limit = 6 } = {}) => {
    const parts = splitPlace(index, text);
    const key = fold(parts.name);
    if (!key) return [];
    const code = parts.country || countryCode(index, country);
    const starts = [];
    const words = [];
    for (const place of index.places) {
      if (code && place.code !== code) continue;
      if (place.key.startsWith(key)) {
        starts.push(place);
        if (starts.length >= limit) break;
      } else if (words.length < limit && place.key.includes(` ${key}`)) {
        words.push(place);
      }
    }
    return starts.concat(words).slice(0, limit).map((place) => ({
      name: place.name,
      code: place.code,
      country: countryName(index, place.code),
    }));
  };

  /** Up to `limit` countries whose name (or another accepted name) starts with the typed text. */
  const suggestCountries = (index, text, { limit = 6 } = {}) => {
    const key = fold(text);
    if (!key) return [];
    const hits = [];
    index.countries.forEach((country) => {
      const shownStarts = fold(country.name).startsWith(key);
      if (shownStarts || country.keys.some((k) => k.startsWith(key))) hits.push({ country, shownStarts });
    });
    hits.sort((a, b) => Number(b.shownStarts) - Number(a.shownStarts) || a.country.name.localeCompare(b.country.name));
    return hits.slice(0, limit).map(({ country }) => ({ code: country.code, country: country.name }));
  };

  /**
   * What the form should do with a place and country before sending.
   * status: 'found' | 'coordinates' | 'empty' | 'country-unknown' | 'country-mismatch' | 'not-found'
   * - found: `match` is the place; `fillCountry` is set when the country may be filled in by itself.
   * - country-unknown: the Map Builder would reject the country; `suggestion` is a close country name.
   * - country-mismatch: the name exists, but not in that country; `suggestion` is the largest one.
   * - not-found: not on the list; `suggestion` is a close spelling or a longer name ("New York City").
   */
  const checkPlace = (index, placeText, countryText) => {
    const parts = splitPlace(index, placeText);
    const key = fold(parts.name);
    if (!key) return { status: 'empty' };
    if (isCoordinates(placeText)) return { status: 'coordinates' };

    let code = parts.country;
    if (!code && fold(countryText)) {
      code = countryCode(index, countryText);
      if (!code) {
        const near = closestCountry(index, fold(countryText));
        return { status: 'country-unknown', suggestion: near ? { code: near, country: countryName(index, near) } : null };
      }
    }

    const show = (place) => view(index, place);
    const same = index.byKey.get(key) || [];

    if (code) {
      const inCountry = same.find((place) => place.code === code);
      if (inCountry) return { status: 'found', match: show(inCountry), fillCountry: null };
      if (same.length) return { status: 'country-mismatch', suggestion: show(same[0]) };
      const near = show(closestPlace(index, key, code)) || suggestPlaces(index, parts.name, { country: code, limit: 1 })[0];
      return { status: 'not-found', suggestion: near || null };
    }

    if (same.length) {
      const [first, second] = same;
      const clear = !second || first.population >= second.population * DOMINANCE;
      return { status: 'found', match: show(first), fillCountry: clear ? show(first) : null };
    }
    const near = show(closestPlace(index, key, null)) || suggestPlaces(index, parts.name, { limit: 1 })[0];
    return { status: 'not-found', suggestion: near || null };
  };

  /** Places with exactly this name, one per country, largest first ("Barcelona" → Spain, Venezuela, …). */
  const placesNamed = (index, text, limit = 3) =>
    (index.byKey.get(fold(splitPlace(index, text).name)) || []).slice(0, limit).map((place) => view(index, place));

  /** True when the place text already settles the country: coordinates, or "City, Country". */
  const countryInPlace = (index, text) => isCoordinates(text) || Boolean(index && splitPlace(index, text).country);

  const api = { fold, isCoordinates, createIndex, countryCode, countryName, splitPlace, suggestPlaces, suggestCountries, checkPlace, placesNamed, countryInPlace, distance };

  // window in the browser; globalThis also lets the Node tests load this file (tests/unit).
  globalThis.AoctantPlaceSearch = api;
})();
